import React, { forwardRef, useMemo, useRef, useState } from 'react';

import { Popup } from '@mobiscroll/react';
import { Box } from '@mui/material';

import { CalendarSmallIcon } from '@setup/svg-icons/svg-icons';
import { MuiBaseTextFiled } from '@shared/ui-kit';
import DatePickerPopupContent from '@shared/ui-kit/pickers/DatePickerRangeWithPresets/components/DatePickerPopupContent';
import { getSelectedPresent } from '@shared/ui-kit/pickers/DatePickerRangeWithPresets/utils/getSelectedPresent';
import { formatDateForView } from '@shared/utils/formatDateForView';

import type { FormSubmitHandlerModel } from '@shared/models/formSubmitHandler.model';
import type { Moment } from 'moment/moment';

type DatePickerProps = {
  variant?: 'iconButton' | 'input' | 'button';
  isDisabled?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isFullWidth?: boolean;
  isShowIcon?: boolean;
  isError?: boolean;
  label?: string;
  placeholder?: string;
  value?: any;
  helperText?: string;
  errorMessage?: string;
  size?: 'small' | 'medium';
  dateFormat?: string;
  onChange: (date: Moment[] | null) => void;
  isClearable?: boolean;
  [x: string]: any;
};

export const DatePickerRangeWithPresets = forwardRef<HTMLHeadingElement, DatePickerProps>((props, ref) => {
  const { value, dateFormat, onChange, isShowIcon, ...args } = props;

  const [isOpen, setOpen] = useState<boolean>(false);
  const [input, inputRef] = useState<boolean>(false);

  const imperativeRef = useRef<FormSubmitHandlerModel>(null);

  const handleImperativeSave = () => {
    imperativeRef.current?.submit();
  };

  const handleClearValue = () => {
    onChange(null);
  };

  const respPopup = useMemo(
    () => ({
      xsmall: {
        display: 'bottom',
        touchUi: true,
      },
      custom: {
        breakpoint: 600,
        buttons: [],
        display: 'anchored',
        anchor: input,
        anchorAlign: 'center',
        touchUi: false,
        scrollLock: false,
        showArrow: false,
        maxWidth: 920,
      },
    }),
    [input],
  );

  const inputClick = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const onCloseWithSave = () => {
    setOpen(false);
    handleImperativeSave();
  };

  const inputValue = useMemo(() => {
    const presetValue = value?.[0] ? getSelectedPresent(value[0], value[1]) : null;
    return presetValue && presetValue.value !== 'custom'
      ? presetValue.label
      : !!value?.[0]
        ? `${formatDateForView(value[0], dateFormat || 'MMM DD YYYY')} - ${formatDateForView(
            value?.[1],
            dateFormat || 'MMM DD YYYY',
          )}`
        : '';
  }, [dateFormat, value]);

  return (
    <Box ref={inputRef}>
      <MuiBaseTextFiled
        {...args}
        variant="outlined"
        value={inputValue}
        ref={ref}
        defaultValue={undefined}
        onClick={inputClick}
        onChange={handleClearValue}
        inputProps={{
          readOnly: true,
          startAdornment: isShowIcon ? <CalendarSmallIcon /> : '',
        }}
      />

      <Popup
        isOpen={isOpen}
        onClose={onCloseWithSave}
        // @ts-ignore
        responsive={respPopup}
      >
        <DatePickerPopupContent
          value={value}
          onChange={onChange}
          onClose={onClose}
          dateFormat={dateFormat}
          ref={imperativeRef}
        />
      </Popup>
    </Box>
  );
});

export default DatePickerRangeWithPresets;
